import React, {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {Box, useTheme, useMediaQuery, Container, Paper} from "@mui/material";
import {
  TransactionChart,
  DashboardCard,
  TableList,
  Spinner,
} from "../../components";
import CustomizedSnackbars from "../../components/base/Snackbar";
import moment from "moment";
import api from "../../helpers/api";
import {tableHeadDashboard} from "../../utils/Constants";

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isSpinner, setIsSpinner] = useState(false);

  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [productProvitList, setProductProvitList] = useState(null);
  const [branchList, setBranchList] = useState([]);
  const [searchBranchName, setSearchBranchName] = useState("");
  const [branchCodeSelected, setBranchCodeSelected] = useState("");

  // State for date filters
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [transactionCode, setTransactionType] = useState("");

  // =====================

  const onGetProductSelling = async () => {
    setIsLoading(true);
    const currentPage = page + 1;
    const dateFromSelected =
      selectedDateFrom === null
        ? ""
        : moment(selectedDateFrom).format("YYYY-MM-DD");
    const dateToSelected =
      selectedDateTo === null
        ? ""
        : moment(selectedDateTo).format("YYYY-MM-DD");
    try {
      const res = await api.getProductProvit(
        searchBranchName,
        dateFromSelected,
        dateToSelected,
        rowsPerPage,
        currentPage
      );
      if (res.statusCode === 200) {
        setTotalRecord(res.data.totalData * res.data.limit);
        setProductProvitList(res.data.data);
      } else {
        setProductProvitList([]);
        onError(res.message);
      }
    } catch (error) {
      setProductProvitList([]);
      onError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      await onGetProductSelling();
    };
    fetch();
  }, [page, rowsPerPage]);

  const onError = async (message) => {
    setProductProvitList([]);
    setIsLoading(false);
    setIsOpen(true);
    setAlertType("error");
    setMessage(message);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{px: {xs: 1, sm: 2, md: 3}}}>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      {/* Dashboard Card */}
      <Grid
        container
        spacing={{xs: 2, sm: 2, md: 3}}
        sx={{
          marginTop: {xs: 1, sm: 2},
          marginBottom: {xs: 2, sm: 3},
        }}
      >
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            isLoadingShimmer={isLoading}
            titleCard="Total Balance"
            dataCard="Rp. 3.460.000"
            fromColor={"from-indigo-400"}
            toColor={"to-cyan-400"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            isLoadingShimmer={isLoading}
            titleCard={"Total Income"}
            dataCard={"Rp. 3.460.000"}
            fromColor={"from-blue-800"}
            toColor={"to-indigo-900"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            isLoadingShimmer={isLoading}
            titleCard={"Total Expenses"}
            dataCard={"Rp. 460.000"}
            fromColor={"from-emerald-500"}
            toColor={"to-emerald-900"}
          />
        </Grid>
      </Grid>

      {/* Dashboard Statistic */}
      <Paper
        elevation={1}
        sx={{
          padding: {xs: 2, sm: 3},
          marginBottom: {xs: 4, sm: 5},
          borderRadius: 1,
        }}
      >
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          sx={{mb: {xs: 2, sm: 2}}}
          fontWeight="medium"
        >
          Transaction History
        </Typography>

        {/* Menghapus batasan tinggi tetap dan menggunakan min-height */}
        <Box
          sx={{
            minHeight: {xs: "350px", sm: "400px", md: "450px"},
            width: "100%",
            "& .recharts-responsive-container": {
              width: "100% !important",
              height: "100% !important",
            },
          }}
        >
          <TransactionChart />
        </Box>
      </Paper>

      {/* Best Selling Products Table */}
      <Paper
        elevation={1}
        sx={{
          width: "100%",
          backgroundColor: "white",
          borderRadius: 1,
          overflow: "auto",
          mb: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: {xs: "column", sm: "row"},
            justifyContent: "space-between",
            alignItems: {xs: "flex-start", sm: "center"},
            p: {xs: 2, sm: 3},
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{mb: {xs: 1, sm: 0}}}
            fontWeight="medium"
          >
            Best Selling Product
          </Typography>
        </Box>

        {/* table */}
        <Box
          sx={{
            overflowX: "auto",
            "& .MuiTableCell-root": {
              whiteSpace: isMobile ? "nowrap" : "normal",
              px: {xs: 1, sm: 2},
              py: {xs: 1, sm: 1.5},
            },
          }}
        >
          <TableList
            tableStructure={"dashboard-products"}
            tableName={"Best Seller Products"}
            isShowPagination={true}
            isLoading={isLoading}
            tableHeadList={tableHeadDashboard}
            data={productProvitList}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default Dashboard;
